/**
 * Read-time URL rewriter for Wheel Pros gallery images.
 *
 * Why: WheelPros's catalog feed ships gallery URLs on
 * assets.wheelpros.com with `?size=500` baked into every URL. The
 * transform CDN supports arbitrary `?size=NNN`, so bumping to 1500 (or
 * any target) returns a much larger image with zero ingest cost. We
 * apply this at READ time (not at write time) so:
 *   - we never mutate stored DB rows
 *   - reverting is trivial (delete this file's call sites)
 *   - if WP changes their CDN behavior we can adjust without a backfill
 *
 * What it touches:
 *   - assets.wheelpros.com URLs with an existing `size=NNN` query param
 *     → rewrites only the `size` param
 *   - assets.wheelpros.com URLs with NO size param → leaves alone
 *     (some endpoints serve original size by default; we don't want to
 *     impose a size on those)
 *   - media.wheelpros.com URLs (full-res original, e.g.
 *     /m/.../original/...png) → unchanged; already full-res
 *   - any other URL (ATD, Vision, manual upload) → unchanged
 *   - null / empty / undefined → returned as-is
 *
 * Pure function. No side effects. Caller passes a target size if they
 * want something other than 1500.
 */

const DEFAULT_TARGET_SIZE = 1500;

export function upsizeWpImage(url: string | null | undefined, target = DEFAULT_TARGET_SIZE): string | null {
  if (url == null || url === '') return url ?? null;
  // Cheap host check — only the assets.wheelpros.com transform endpoint
  // accepts the size param. Other hosts get returned untouched.
  if (!url.includes('assets.wheelpros.com')) return url;
  // Replace ONLY when there's an existing size= param. Don't append
  // to a URL that didn't have one — those endpoints might serve the
  // already-original image.
  return url.replace(/(\?|&)size=\d+/i, (match) => {
    const sep = match.startsWith('?') ? '?' : '&';
    return `${sep}size=${target}`;
  });
}
